import { computed, ComputedRef, ref, watch } from 'vue'

import {
  Coordinates,
  hasIncidentPosition,
  isIncidentEditable,
  isSameTime,
} from '@collector/shared-ui-domain'
import {
  Confirmation,
  EventStatusId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { IncidentsRequirePointingPositionTable } from '@desktop/utils/database'
import {
  useEvent,
  useEventSettings,
  useIncidents,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import { injectIncidentsRequirePointingPositionConfig } from '@desktop/views/Relation/Sports/provide-inject'

export function useIncidentsRequirePointingPosition(): {
  currentIncident: ComputedRef<Incident | null>
  init: () => Promise<void>
  getTransformedCoordinates: (
    coordinates: Coordinates,
    incident: Incident,
  ) => Coordinates
  getChainedIncident: () => Incident | null
  removeIncidentId: (id: Incident['id']) => Promise<void>
} {
  // inject
  const { pointingPositionIncidents, excludedIncidentEventStatuses } =
    injectIncidentsRequirePointingPositionConfig()

  // composables
  const { event } = useEvent()

  const { incidents, incidentIdIncidentIndexMap, userIncidentsUpdate } =
    useIncidents()
  const { eventSettings } = useEventSettings()
  const { getIncidentTeamSide } = useTeamSides()

  // state
  const incidentsRequirePointingPosition = ref<Incident['id'][]>([])

  // computed
  const currentIncident = computed(() => {
    const id = incidentsRequirePointingPosition.value[0]

    if (id) {
      const incidentIndex = incidentIdIncidentIndexMap.value.get(id)

      if (incidentIndex !== undefined) {
        return incidents.value[incidentIndex]
      }
    }

    return null
  })

  // methods
  function getChainedIncident(): Incident | null {
    if (!currentIncident.value) {
      return null
    }

    const incidentConfig =
      pointingPositionIncidents[currentIncident.value.incident_id]

    if (!incidentConfig || !incidentConfig.chainedIncidents) {
      return null
    }

    const incidentIndex = incidentIdIncidentIndexMap.value.get(
      currentIncident.value.id,
    )

    if (incidentIndex === undefined || incidentIndex === 0) {
      return null
    }

    const chainedIncident = incidents.value[incidentIndex - 1]

    if (
      incidentConfig.chainedIncidents.includes(chainedIncident.incident_id) &&
      isSameTime(currentIncident.value, chainedIncident) &&
      currentIncident.value.source_id === chainedIncident.source_id &&
      isIncidentEditable(chainedIncident) &&
      !hasIncidentPosition(chainedIncident)
    ) {
      return chainedIncident
    }

    return null
  }

  function getTransformedCoordinates(
    coordinates: Coordinates,
    incident: Incident,
  ): Coordinates {
    const incidentConfig = pointingPositionIncidents[incident.incident_id]

    if (!incidentConfig || !incidentConfig.transformCoordinates) {
      return coordinates
    }

    const teamSide = getIncidentTeamSide(incident)

    return incidentConfig.transformCoordinates(coordinates, teamSide!)
  }

  async function init(): Promise<void> {
    const incidentsRequirePointingPositionRecord =
      await IncidentsRequirePointingPositionTable.get(event.value.id)

    if (incidentsRequirePointingPositionRecord) {
      incidentsRequirePointingPosition.value.push(
        ...incidentsRequirePointingPositionRecord.incidents,
      )
    }
  }

  async function removeIncidentId(id: Incident['id']): Promise<void> {
    const index = incidentsRequirePointingPosition.value.findIndex(
      (value) => value === id,
    )

    if (index !== -1) {
      incidentsRequirePointingPosition.value.splice(index, 1)

      return IncidentsRequirePointingPositionTable.deleteIncident(
        event.value.id,
        id,
      )
    }
  }

  function saveIncidentId(id: Incident['id']): Promise<void> {
    incidentsRequirePointingPosition.value.push(id)

    return IncidentsRequirePointingPositionTable.upsert(event.value.id, [
      ...incidentsRequirePointingPosition.value,
    ])
  }

  function isIncidentRequirePointingPosition(
    incidentId: IncidentId,
    statusId: EventStatusId,
  ): boolean {
    const incidentConfig = pointingPositionIncidents[incidentId]

    if (
      incidentConfig === undefined ||
      excludedIncidentEventStatuses.includes(statusId)
    ) {
      return false
    }

    return (
      incidentConfig === null ||
      !incidentConfig.shouldRequireUserPointingPosition ||
      incidentConfig?.shouldRequireUserPointingPosition(eventSettings.value)
    )
  }

  // init
  watch(userIncidentsUpdate, async (incidentMessages) => {
    for (const incidentMessage of incidentMessages) {
      // 'delete' and 'update' for all incident ids,
      // because user can switch off setting and must be able to complete the action
      switch (incidentMessage.data.action) {
        case 'insert':
          if (
            isIncidentRequirePointingPosition(
              incidentMessage.data.incident_id,
              incidentMessage.data.status_id,
            )
          ) {
            await saveIncidentId(incidentMessage.data.id)
          }
          break

        case 'update':
          if (
            hasIncidentPosition(incidentMessage.data) ||
            incidentMessage.data.confirmation === Confirmation.Cancelled
          ) {
            await removeIncidentId(incidentMessage.data.id)
          }
          break

        case 'delete':
          await removeIncidentId(incidentMessage.data.id)
          break
      }
    }
  })

  return {
    currentIncident,
    init,
    getTransformedCoordinates,
    getChainedIncident,
    removeIncidentId,
  }
}
